/**
 * Ringtones for incoming WhatsApp calls — played by Setu in the agent's
 * browser. (The customer's phone always uses their own WhatsApp ringtone;
 * Meta offers no way to change that.)
 *
 * Built-in tones are synthesized with WebAudio, so they ship no audio files.
 * An agent may also pick a sound file of their own; it is kept in this
 * browser only (see ringtoneStore), never uploaded.
 */

export type ToneId = 'chime' | 'classic' | 'digital' | 'marimba' | 'bell' | 'silent' | 'custom'

export interface ToneDef {
  id: Exclude<ToneId, 'custom'>
  label: string
  hint: string
  /** Seconds between ring repeats. */
  cycle: number
  /** Schedules one ring of the tone starting at `t0` on `out`. */
  play: (ctx: BaseAudioContext, out: AudioNode, t0: number) => void
}

interface NoteOptions {
  type?: OscillatorType
  peak?: number
  /** Hold full level until the end (phone ring) instead of decaying (bell). */
  hold?: boolean
}

function note(
  ctx: BaseAudioContext,
  out: AudioNode,
  freq: number,
  start: number,
  duration: number,
  { type = 'sine', peak = 0.5, hold = false }: NoteOptions = {},
) {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = type
  osc.frequency.value = freq
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012)
  if (hold) gain.gain.setValueAtTime(peak, start + duration - 0.03)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  osc.connect(gain).connect(out)
  osc.start(start)
  osc.stop(start + duration + 0.02)
}

export const TONES: readonly ToneDef[] = [
  {
    id: 'chime',
    label: 'Setu chime',
    hint: 'Two soft rising notes',
    cycle: 2.6,
    play: (ctx, out, t0) => {
      for (const at of [0, 0.9]) {
        note(ctx, out, 659.25, t0 + at, 0.6, { type: 'triangle' })
        note(ctx, out, 987.77, t0 + at + 0.18, 0.7, { type: 'triangle' })
      }
    },
  },
  {
    id: 'classic',
    label: 'Classic phone',
    hint: 'Double ring of a desk phone',
    cycle: 3,
    play: (ctx, out, t0) => {
      for (const at of [0, 0.6]) {
        note(ctx, out, 440, t0 + at, 0.4, { peak: 0.22, hold: true })
        note(ctx, out, 480, t0 + at, 0.4, { peak: 0.22, hold: true })
      }
    },
  },
  {
    id: 'digital',
    label: 'Digital pulse',
    hint: 'Short, sharp beeps',
    cycle: 1.8,
    play: (ctx, out, t0) => {
      for (const at of [0, 0.12, 0.24, 0.36]) {
        note(ctx, out, 1046.5, t0 + at, 0.07, { type: 'square', peak: 0.1, hold: true })
      }
    },
  },
  {
    id: 'marimba',
    label: 'Marimba',
    hint: 'Four wooden notes up the scale',
    cycle: 2.2,
    play: (ctx, out, t0) => {
      ;[523.25, 659.25, 783.99, 1046.5].forEach((freq, i) =>
        note(ctx, out, freq, t0 + i * 0.13, 0.35),
      )
    },
  },
  {
    id: 'bell',
    label: 'Desk bell',
    hint: 'One clear ring that fades',
    cycle: 2.6,
    play: (ctx, out, t0) => {
      note(ctx, out, 880, t0, 1.6, { peak: 0.45 })
      note(ctx, out, 1760, t0, 1, { peak: 0.15 })
      note(ctx, out, 2637, t0, 0.6, { peak: 0.07 })
    },
  },
  {
    id: 'silent',
    label: 'Silent',
    hint: 'Card only, no sound',
    cycle: 0,
    play: () => {},
  },
]

export const DEFAULT_TONE: ToneId = 'chime'

/** Largest own sound accepted — it is stored in this browser's storage. */
export const CUSTOM_SOUND_MAX_BYTES = 1024 * 1024

export interface RingtoneChoice {
  toneId: ToneId
  /** 0–100. */
  volume: number
  custom: { name: string; dataUrl: string } | null
}

interface PlayOptions {
  loop?: boolean
  /** Called when the browser refuses to play sound in this tab (it allows
   *  it only after the user has clicked or typed on the page once). */
  onBlocked?: () => void
}

/** How long a just-started sound may stay suspended before we call it
 *  blocked. */
const BLOCKED_CHECK_MS = 300

/** Starts the ringtone and returns a function that stops it. Never throws:
 *  a blocked autoplay or missing audio device just means no sound — the
 *  call card still shows. */
export function playRingtone(
  choice: RingtoneChoice,
  { loop = true, onBlocked }: PlayOptions = {},
): () => void {
  const level = Math.max(0, Math.min(100, choice.volume)) / 100
  if (level === 0 || choice.toneId === 'silent') return () => {}

  if (choice.toneId === 'custom' && choice.custom) {
    try {
      const audio = new Audio(choice.custom.dataUrl)
      audio.loop = loop
      audio.volume = level
      void audio.play().catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name === 'NotAllowedError') onBlocked?.()
      })
      return () => {
        audio.pause()
        audio.removeAttribute('src')
      }
    } catch {
      return () => {}
    }
  }

  const tone = TONES.find((t) => t.id === choice.toneId) ?? TONES[0]
  try {
    const ctx = new AudioContext()
    void ctx.resume().catch(() => {})
    const master = ctx.createGain()
    master.gain.value = level
    master.connect(ctx.destination)
    const ring = () => tone.play(ctx, master, ctx.currentTime + 0.02)
    ring()
    const timer = loop ? setInterval(ring, tone.cycle * 1000) : null
    const check = onBlocked
      ? setTimeout(() => {
          if (ctx.state === 'suspended') onBlocked()
        }, BLOCKED_CHECK_MS)
      : null
    return () => {
      if (timer) clearInterval(timer)
      if (check) clearTimeout(check)
      void ctx.close().catch(() => {})
    }
  } catch {
    return () => {}
  }
}

/** Ringback level relative to the agent's ringtone volume — it plays in
 *  their headset while they wait, so it sits well below a ring. */
const RINGBACK_LEVEL = 0.6

/**
 * Ringback: the "tring-tring" a caller hears while the other phone rings —
 * two short 400 + 450 Hz bursts, then a pause, as on Indian and UK lines.
 * Meta sends no audio before the customer answers, so Setu plays it while
 * an agent's call is ringing. Returns a function that stops it; never throws.
 */
export function playRingback(volume: number): () => void {
  const level = (Math.max(0, Math.min(100, volume)) / 100) * RINGBACK_LEVEL
  if (level === 0) return () => {}
  try {
    const ctx = new AudioContext()
    void ctx.resume().catch(() => {})
    const master = ctx.createGain()
    master.gain.value = level
    master.connect(ctx.destination)
    const ring = () => {
      const t0 = ctx.currentTime + 0.02
      for (const at of [0, 0.6]) {
        note(ctx, master, 400, t0 + at, 0.4, { peak: 0.2, hold: true })
        note(ctx, master, 450, t0 + at, 0.4, { peak: 0.2, hold: true })
      }
    }
    ring()
    const timer = setInterval(ring, 3000)
    return () => {
      clearInterval(timer)
      void ctx.close().catch(() => {})
    }
  } catch {
    return () => {}
  }
}
